import { useState, useEffect, useCallback } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { MapPin, Loader2, RefreshCw, CalendarIcon, Clock } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { GooglePlacesAutocomplete } from "./GooglePlacesAutocomplete";

interface Trip {
  id: string;
  vehicle_id: string;
  driver_id: string | null;
  origin: string | null;
  destination: string | null;
  eta_start: string | null;
  eta_end: string | null;
  status: string;
  distance_planned_km: number | null;
}

interface TripFormSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  companyId: string;
  mode?: 'create' | 'edit';
  initialData?: Trip | null;
}

export function TripFormSheet({ 
  open, 
  onOpenChange, 
  companyId, 
  mode = 'create', 
  initialData = null 
}: TripFormSheetProps) {
  const { profile } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  const [formData, setFormData] = useState({
    vehicle_id: "",
    driver_id: "",
    origin: "",
    origin_lat: null as number | null,
    origin_lng: null as number | null,
    destination: "",
    destination_lat: null as number | null,
    destination_lng: null as number | null,
    eta_start_date: undefined as Date | undefined,
    eta_start_time: "",
    eta_end_date: undefined as Date | undefined,
    eta_end_time: "",
    distance_planned_km: "",
    status: "planned",
  });

  const [isCalculatingRoute, setIsCalculatingRoute] = useState(false);
  const [routeInfo, setRouteInfo] = useState<{
    distance: number;
    duration: number;
    distance_text: string;
    duration_text: string;
  } | null>(null);
  const [isOriginVerified, setIsOriginVerified] = useState(false);
  const [isDestinationVerified, setIsDestinationVerified] = useState(false);
  const [hasCalculatedRoute, setHasCalculatedRoute] = useState(false);

  const { data: vehicles } = useQuery({
    queryKey: ["fleetVehicles", companyId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("fleet_vehicles")
        .select("id, plate, make, model")
        .eq("company_id", companyId)
        .eq("status", "active");

      if (error) throw error;
      return data;
    },
    enabled: !!companyId && open,
  });

  const { data: drivers } = useQuery({
    queryKey: ["fleetDrivers", companyId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("fleet_drivers")
        .select("id, name")
        .eq("company_id", companyId)
        .eq("status", "active");

      if (error) throw error;
      return data;
    },
    enabled: !!companyId && open,
  });

  useEffect(() => {
    if (initialData && mode === 'edit') {
      const parseDateTime = (isoString: string | null) => {
        if (!isoString) return { date: undefined, time: "" };
        const date = new Date(isoString);
        return {
          date: date,
          time: format(date, 'HH:mm')
        };
      };
      
      const startParsed = parseDateTime(initialData.eta_start);
      const endParsed = parseDateTime(initialData.eta_end);

      setFormData({
        vehicle_id: initialData.vehicle_id || "",
        driver_id: initialData.driver_id || "",
        origin: initialData.origin || "",
        origin_lat: null,
        origin_lng: null,
        destination: initialData.destination || "",
        destination_lat: null,
        destination_lng: null,
        eta_start_date: startParsed.date,
        eta_start_time: startParsed.time,
        eta_end_date: endParsed.date,
        eta_end_time: endParsed.time,
        distance_planned_km: initialData.distance_planned_km?.toString() || "",
        status: initialData.status || "planned",
      });
    } else {
      resetForm();
    }
  }, [initialData, mode, open]);

  const handleOriginSelect = useCallback((place: {
    address: string;
    lat: number;
    lng: number;
    place_id: string;
  }) => {
    setFormData(prev => ({
      ...prev,
      origin: place.address,
      origin_lat: place.lat,
      origin_lng: place.lng,
    }));
    
    setIsOriginVerified(true);
    
    toast({
      title: "✓ Origem verificada",
      description: place.address,
    });
  }, [toast]);

  const handleDestinationSelect = useCallback((place: {
    address: string;
    lat: number;
    lng: number;
    place_id: string;
  }) => {
    setFormData(prev => ({
      ...prev,
      destination: place.address,
      destination_lat: place.lat,
      destination_lng: place.lng,
    }));
    
    setIsDestinationVerified(true);
    
    toast({
      title: "✓ Destino verificado",
      description: place.address,
    });
  }, [toast]);

  const calculateRoute = useCallback(async () => {
    console.log('🚀 calculateRoute() executada');
    
    if (!formData.origin_lat || !formData.origin_lng || !formData.destination_lat || !formData.destination_lng) {
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Selecione origem e destino nos campos de autocomplete.",
      });
      return;
    }

    setIsCalculatingRoute(true);
    try {
      const { data, error } = await supabase.functions.invoke('google-directions', {
        body: {
          origin: { lat: formData.origin_lat, lng: formData.origin_lng },
          destination: { lat: formData.destination_lat, lng: formData.destination_lng }
        }
      });

      if (error) throw error;

      if (data && data.distance_km && data.duration_min) {
        const distanceKm = data.distance_km;
        const durationMinutes = data.duration_min;
        const hours = Math.floor(durationMinutes / 60);
        const minutes = durationMinutes % 60;
        
        setRouteInfo({
          distance: distanceKm,
          duration: durationMinutes * 60, // converter para segundos para compatibilidade
          distance_text: data.distance_text,
          duration_text: data.duration_text
        });

        setFormData(prev => {
          const updates: any = {
            ...prev,
            distance_planned_km: distanceKm.toString()
          };
          
          // Se temos início previsto, calcular fim previsto
          if (prev.eta_start_date && prev.eta_start_time) {
            const [hours, minutes] = prev.eta_start_time.split(':').map(Number);
            const startDate = new Date(prev.eta_start_date);
            startDate.setHours(hours, minutes, 0, 0);
            
            // Adicionar duração da viagem
            const endDate = new Date(startDate.getTime() + (durationMinutes * 60 * 1000));
            
            updates.eta_end_date = endDate;
            updates.eta_end_time = format(endDate, 'HH:mm');
          }
          
          return updates;
        });

        toast({
          title: "Rota calculada!",
          description: `Distância: ${data.distance_text} • Duração estimada: ${data.duration_text}`,
        });
        
        setHasCalculatedRoute(true);
      }
    } catch (error: any) {
      console.error('Erro ao calcular rota:', error);
      toast({
        variant: "destructive",
        title: "Erro ao calcular rota",
        description: error.message || "Não foi possível calcular a rota.",
      });
    } finally {
      setIsCalculatingRoute(false);
    }
  }, [formData.origin_lat, formData.origin_lng, formData.destination_lat, formData.destination_lng, toast]);

  // Cálculo automático de rota quando origem e destino estiverem preenchidos
  useEffect(() => {
    console.log('🔍 useEffect cálculo automático acionado:', {
      origin_lat: formData.origin_lat,
      origin_lng: formData.origin_lng,
      destination_lat: formData.destination_lat,
      destination_lng: formData.destination_lng,
      isCalculatingRoute
    });

    if (
      formData.origin_lat &&
      formData.origin_lng &&
      formData.destination_lat &&
      formData.destination_lng &&
      !isCalculatingRoute &&
      !hasCalculatedRoute
    ) {
      console.log('✅ Todas as condições atendidas, calculando rota automaticamente...');
      calculateRoute();
    }
  }, [
    formData.origin_lat,
    formData.origin_lng,
    formData.destination_lat,
    formData.destination_lng,
    isCalculatingRoute,
    hasCalculatedRoute,
    calculateRoute
  ]);

  // Resetar flag quando origem mudar
  useEffect(() => {
    setHasCalculatedRoute(false);
  }, [formData.origin_lat, formData.origin_lng]);

  // Resetar flag quando destino mudar  
  useEffect(() => {
    setHasCalculatedRoute(false);
  }, [formData.destination_lat, formData.destination_lng]);

  const createTripMutation = useMutation({
    mutationFn: async (tripData: any) => {
      const { data, error } = await supabase
        .from("fleet_trips")
        .insert({
          ...tripData,
          company_id: companyId,
          created_by: profile?.id
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["fleetTrips"] });
      onOpenChange(false);
      toast({
        title: "Viagem criada!",
        description: "A viagem foi cadastrada com sucesso.",
      });
      resetForm();
    },
    onError: (error: Error) => {
      toast({
        variant: "destructive",
        title: "Erro ao criar viagem",
        description: error.message,
      });
    },
  });

  const updateTripMutation = useMutation({
    mutationFn: async (tripData: any) => {
      if (!initialData?.id) throw new Error("ID da viagem não encontrado");

      const { data, error } = await supabase
        .from("fleet_trips")
        .update(tripData)
        .eq("id", initialData.id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["fleetTrips"] });
      onOpenChange(false);
      toast({
        title: "Viagem atualizada!",
        description: "As alterações foram salvas com sucesso.",
      });
    },
    onError: (error: Error) => {
      toast({
        variant: "destructive",
        title: "Erro ao atualizar viagem",
        description: error.message,
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.vehicle_id) {
      toast({
        variant: "destructive",
        title: "Campo obrigatório",
        description: "Selecione um veículo para a viagem.",
      });
      return;
    }

    if (!formData.origin || !formData.destination) {
      toast({
        variant: "destructive",
        title: "Campos obrigatórios",
        description: "Preencha origem e destino da viagem.",
      });
      return;
    }

    if (!formData.eta_start_date || !formData.eta_start_time) {
      toast({
        variant: "destructive",
        title: "Campo obrigatório",
        description: "Preencha a data e hora de início previsto da viagem.",
      });
      return;
    }

    const combineDateTime = (date: Date | undefined, time: string): string | null => {
      if (!date || !time) return null;
      const [hours, minutes] = time.split(':');
      const combined = new Date(date);
      combined.setHours(parseInt(hours), parseInt(minutes), 0, 0);
      return combined.toISOString();
    };

    const tripData = {
      vehicle_id: formData.vehicle_id,
      driver_id: formData.driver_id || null,
      origin: formData.origin,
      destination: formData.destination,
      eta_start: combineDateTime(formData.eta_start_date, formData.eta_start_time),
      eta_end: combineDateTime(formData.eta_end_date, formData.eta_end_time),
      distance_planned_km: formData.distance_planned_km ? parseFloat(formData.distance_planned_km) : null,
      status: formData.status,
    };

    if (mode === 'create') {
      createTripMutation.mutate(tripData);
    } else {
      updateTripMutation.mutate(tripData);
    }
  };

  const resetForm = () => {
    const now = new Date();
    const currentTime = format(now, 'HH:mm');
    
    setFormData({
      vehicle_id: "",
      driver_id: "",
      origin: "",
      origin_lat: null,
      origin_lng: null,
      destination: "",
      destination_lat: null,
      destination_lng: null,
      eta_start_date: now,
      eta_start_time: currentTime,
      eta_end_date: undefined,
      eta_end_time: "",
      distance_planned_km: "",
      status: "planned",
    });
    setRouteInfo(null);
    setIsOriginVerified(false);
    setIsDestinationVerified(false);
    setHasCalculatedRoute(false);
  };

  const isLoading = createTripMutation.isPending || updateTripMutation.isPending;

  return (
    <Sheet open={open} onOpenChange={onOpenChange} modal={false}>
      <SheetContent 
        side="right" 
        className="sm:max-w-[700px] overflow-hidden flex flex-col"
        onInteractOutside={(e) => {
          // Previne fechamento ao interagir com Google Places dropdown
          const target = e.target as HTMLElement;
          if (target.closest('.pac-container') || 
              target.closest('.pac-item') ||
              target.classList.contains('pac-container')) {
            e.preventDefault();
          }
        }}
        onEscapeKeyDown={(e) => {
          // Confirmação antes de fechar com ESC se houver dados não salvos
          if (formData.origin || formData.destination || formData.vehicle_id) {
            const confirmClose = window.confirm('Deseja fechar sem salvar as alterações?');
            if (!confirmClose) {
              e.preventDefault();
            }
          }
        }}
      >
        <SheetHeader>
          <SheetTitle>{mode === 'create' ? 'Nova Viagem' : 'Editar Viagem'}</SheetTitle>
          <SheetDescription>
            {mode === 'create' 
              ? 'Configure os detalhes da nova viagem'
              : 'Atualize as informações da viagem'
            }
          </SheetDescription>
        </SheetHeader>

        <ScrollArea className="flex-1 -mx-6 px-6">
          <form id="trip-form" onSubmit={handleSubmit} className="space-y-6 py-4">
            {/* Veículo e Motorista */}
            <div className="space-y-4">
              <h3 className="font-medium text-sm">Veículo e Motorista</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="vehicle_id">Veículo *</Label>
                  <Select
                    value={formData.vehicle_id}
                    onValueChange={(value) => setFormData({ ...formData, vehicle_id: value })}
                  >
                    <SelectTrigger id="vehicle_id">
                      <SelectValue placeholder="Selecione o veículo" />
                    </SelectTrigger>
                    <SelectContent>
                      {vehicles?.map((vehicle) => (
                        <SelectItem key={vehicle.id} value={vehicle.id}>
                          {vehicle.plate} - {vehicle.make} {vehicle.model}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="driver_id">Motorista</Label>
                  <Select
                    value={formData.driver_id}
                    onValueChange={(value) => setFormData({ ...formData, driver_id: value })}
                  >
                    <SelectTrigger id="driver_id">
                      <SelectValue placeholder="Selecione (opcional)" />
                    </SelectTrigger>
                    <SelectContent>
                      {drivers?.map((driver) => (
                        <SelectItem key={driver.id} value={driver.id}>
                          {driver.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Rota */}
            <div className="space-y-4 pt-4 border-t">
              <h3 className="font-medium text-sm">Rota</h3>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Origem *</Label>
                  <GooglePlacesAutocomplete
                    value={formData.origin}
                    onChange={(address) => setFormData({ ...formData, origin: address })}
                    onPlaceSelect={handleOriginSelect}
                    placeholder="Digite o endereço de origem"
                  />
                  {isOriginVerified && formData.origin_lat && formData.origin_lng && (
                    <Badge variant="outline" className="text-green-600 border-green-600">
                      <MapPin className="h-3 w-3 mr-1" />
                      ✓ Origem verificada
                    </Badge>
                  )}
                </div>

                <div className="space-y-2">
                  <Label>Destino *</Label>
                  <GooglePlacesAutocomplete
                    value={formData.destination}
                    onChange={(address) => setFormData({ ...formData, destination: address })}
                    onPlaceSelect={handleDestinationSelect}
                    placeholder="Digite o endereço de destino"
                  />
                  {isDestinationVerified && formData.destination_lat && formData.destination_lng && (
                    <Badge variant="outline" className="text-green-600 border-green-600">
                      <MapPin className="h-3 w-3 mr-1" />
                      ✓ Destino verificado
                    </Badge>
                  )}
                </div>
              </div>
            </div>

            {/* Distância Planejada */}
            <div className="space-y-4 pt-4 border-t">
              <div className="flex items-center justify-between">
                <h3 className="font-medium text-sm">Distância Planejada (km)</h3>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setHasCalculatedRoute(false);
                    calculateRoute();
                  }}
                  disabled={isCalculatingRoute || !formData.origin_lat || !formData.destination_lat}
                >
                  {isCalculatingRoute ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4" />
                  )}
                </Button>
              </div>
              
              <div className="space-y-3">
                <Input
                  type="number"
                  step="0.01"
                  value={formData.distance_planned_km}
                  onChange={(e) => setFormData({ ...formData, distance_planned_km: e.target.value })}
                  placeholder="Distância em km"
                />
                
                {routeInfo && (
                  <div className="space-y-2 p-3 bg-muted/50 rounded-md">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <span className="text-green-600">✓</span>
                      <span>Auto-calculado via Google Maps</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Badge variant="secondary" className="gap-1">
                        <MapPin className="h-3 w-3" />
                        Distância: {routeInfo.distance_text}
                      </Badge>
                      <Badge variant="secondary" className="gap-1">
                        <Clock className="h-3 w-3" />
                        Tempo estimado: {routeInfo.duration_text}
                      </Badge>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Programação */}
            <div className="space-y-4 pt-4 border-t">
              <h3 className="font-medium text-sm">Programação</h3>
              
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Início Previsto *</Label>
                  <div className="grid grid-cols-2 gap-4">
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn(
                            "justify-start text-left font-normal",
                            !formData.eta_start_date && "text-muted-foreground"
                          )}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {formData.eta_start_date ? format(formData.eta_start_date, "PPP", { locale: ptBR }) : "Selecione a data"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={formData.eta_start_date}
                          onSelect={(date) => setFormData({ ...formData, eta_start_date: date })}
                          locale={ptBR}
                          initialFocus
                          className="pointer-events-auto"
                        />
                      </PopoverContent>
                    </Popover>

                    <Input
                      type="time"
                      value={formData.eta_start_time}
                      onChange={(e) => setFormData({ ...formData, eta_start_time: e.target.value })}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Fim Previsto</Label>
                  <div className="grid grid-cols-2 gap-4">
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn(
                            "justify-start text-left font-normal",
                            !formData.eta_end_date && "text-muted-foreground"
                          )}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {formData.eta_end_date ? format(formData.eta_end_date, "PPP", { locale: ptBR }) : "Selecione a data"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={formData.eta_end_date}
                          onSelect={(date) => setFormData({ ...formData, eta_end_date: date })}
                          locale={ptBR}
                          initialFocus
                          className="pointer-events-auto"
                        />
                      </PopoverContent>
                    </Popover>

                    <Input
                      type="time"
                      value={formData.eta_end_time}
                      onChange={(e) => setFormData({ ...formData, eta_end_time: e.target.value })}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Status */}
            <div className="space-y-4 pt-4 border-t">
              <h3 className="font-medium text-sm">Status</h3>
              <div className="space-y-2">
                <Select
                  value={formData.status}
                  onValueChange={(value) => setFormData({ ...formData, status: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="planned">Planejada</SelectItem>
                    <SelectItem value="in_progress">Em Andamento</SelectItem>
                    <SelectItem value="completed">Concluída</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </form>
        </ScrollArea>

        <SheetFooter className="border-t pt-4 mt-auto">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            form="trip-form"
            disabled={isLoading}
          >
            {isLoading ? "Salvando..." : mode === 'create' ? "Criar Viagem" : "Salvar Alterações"}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
